import { useCallback, useState } from 'react'
import type { CanvasStateOutput } from '../../types/infrastructure-map'
import type { UseCanvasStatesResult } from './use-canvas-states'

export interface PendingExpandConfirmation {
  endpointId: string
  isExpanding: boolean
  affectedCount: number
}

export interface UseCollapseActionsOptions {
  canvasStates: UseCanvasStatesResult
  childrenByParent: Map<string, string[]>
  getAllDescendants: (endpointId: string) => string[]
}

export interface UseCollapseActionsResult {
  pendingExpandConfirmation: PendingExpandConfirmation | null
  toggleCollapse: (endpointId: string) => Promise<void>
  toggleCollapseRecursive: (endpointId: string) => void
  confirmRecursiveAction: () => Promise<void>
  cancelRecursiveAction: () => void
  getChildrenCount: (endpointId: string) => number
  canCollapse: (endpointId: string) => boolean
}

function readCollapsed(state: CanvasStateOutput | undefined): boolean {
  return state?.isCollapsed ?? false
}

export function useCollapseActions(options: UseCollapseActionsOptions): UseCollapseActionsResult {
  const { canvasStates, childrenByParent, getAllDescendants } = options
  const [pendingExpandConfirmation, setPendingExpandConfirmation] =
    useState<PendingExpandConfirmation | null>(null)

  const getChildrenCount = useCallback(
    (endpointId: string): number => (childrenByParent.get(endpointId) ?? []).length,
    [childrenByParent]
  )

  const canCollapse = useCallback(
    (endpointId: string): boolean => getChildrenCount(endpointId) > 1,
    [getChildrenCount]
  )

  const toggleCollapse = useCallback(
    async (endpointId: string): Promise<void> => {
      const currentState = canvasStates.canvasStatesMap.get(endpointId)
      const newCollapsedState = !readCollapsed(currentState)
      await canvasStates.updateState(endpointId, { isCollapsed: newCollapsedState })
    },
    [canvasStates]
  )

  const toggleCollapseRecursive = useCallback(
    (endpointId: string): void => {
      const currentState = canvasStates.canvasStatesMap.get(endpointId)
      const isCurrentlyCollapsed = readCollapsed(currentState)
      const allDescendants = getAllDescendants(endpointId)
      setPendingExpandConfirmation({
        endpointId,
        isExpanding: isCurrentlyCollapsed,
        affectedCount: allDescendants.length + 1,
      })
    },
    [canvasStates.canvasStatesMap, getAllDescendants]
  )

  const confirmRecursiveAction = useCallback(async (): Promise<void> => {
    if (!pendingExpandConfirmation) return
    const { endpointId, isExpanding } = pendingExpandConfirmation
    const newCollapsedState = !isExpanding
    const allDescendants = getAllDescendants(endpointId)
    await canvasStates.updateState(endpointId, { isCollapsed: newCollapsedState })
    for (const descendantId of allDescendants) {
      if (getChildrenCount(descendantId) > 1) {
        await canvasStates.updateState(descendantId, { isCollapsed: newCollapsedState })
      }
    }
    setPendingExpandConfirmation(null)
  }, [pendingExpandConfirmation, getAllDescendants, canvasStates, getChildrenCount])

  const cancelRecursiveAction = useCallback((): void => {
    setPendingExpandConfirmation(null)
  }, [])

  return {
    pendingExpandConfirmation,
    toggleCollapse,
    toggleCollapseRecursive,
    confirmRecursiveAction,
    cancelRecursiveAction,
    getChildrenCount,
    canCollapse,
  }
}
